import {
  VALID,
  isBlank,
  isValidDateOfService,
  isValidDatePrescribed,
  isValidRXNumber,
  isValidQuantity,
  isQuestionableQuantity,
  isValidFillNumber,
  isValidWholesalerInvoiceNumber,
  isValidNPI,
  isValidNCPDP,
  isValidDEA,
  isValidIdentifier,
  isValidId340b,
  isValidPayerBin,
  isValidPCN,
  isValidAccount340b,
  isValidProductSerializationNumber,
  isValidHCPCS,
  isValidHCPCSMod,
  isValidNonFutureDate,
  isValidDiagnosisCode,
  isValidPlaceOfService,
} from "./DataValidations";
import { isRequiredField } from "../Schemas/PharmacyEncounterClaim";
import SubmissionTranslator from "../../../utils/SubmissionTranslator";

const translator = new SubmissionTranslator("pharmacy_encounter");

const isValidPharmacyEncounterClaimField = (value, column, row, mapping) => {
  if (["formatted_rx_number"].includes(column)) {
    return VALID;
  }

  const requiredResult = checkRequired(column, value);
  if (!requiredResult.isValid) {
    return requiredResult;
  }

  switch (column) {
    case "date_of_service":
      if (!isValidNonFutureDate(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_date"),
        };
      }

      if (!isValidDateOfService(value, row[mapping["date_prescribed"]])) {
        return {
          isValid: false,
          message: translator.getError("date_before_prescribed"),
        };
      }
      return VALID;

    case "date_prescribed":
      if (!isValidNonFutureDate(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_date"),
        };
      }

      if (!isValidDatePrescribed(value, row[mapping["date_of_service"]])) {
        return {
          isValid: false,
          message: translator.getError("date_before_prescribed"),
        };
      }
      return VALID;

    case "rx_number":
      if (!isValidRXNumber(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_rx_number"),
        };
      }
      return VALID;

    case "ndc":
      return VALID;

    case "quantity":
      if (!isValidQuantity(value)) {
        return { isValid: false, message: translator.getError("invalid_quantity") };
      }
      if (isQuestionableQuantity(value)) {
        return { isValid: false, message: translator.getError("quantity_over_max") };
      }
      return VALID;

    case "fill_number":
      if (!isValidFillNumber(value)) {
        return { isValid: false, message: translator.getError("invalid_fill_number") };
      }
      return VALID;

    case "contracted_entity_id":
      if (!isValidId340b(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_id_340b"),
        };
      }
      return VALID;

    case "payer_bin":
      if (!isValidPayerBin(value)) {
        return { isValid: false, message: translator.getError("invalid_payer_bin") };
      }
      return VALID;

    case "payer_pcn":
      if (!isValidPCN(value)) {
        return { isValid: false, message: translator.getError("invalid_payer_pcn") };
      }
      return VALID;

    case "ship_to_location":
      if (!value) return VALID;
      if (!isValidIdentifier(value)) {
        return { isValid: false, message: translator.getError("invalid_identifier") };
      }
      return VALID;

    case "ship_to_date":
    case "encounter_date_of_service":
      if (!value) return VALID;
      if (!isValidNonFutureDate(value)) {
        return { isValid: false, message: translator.getError("invalid_date") };
      }
      return VALID;

    case "account_340b":
      if (!isValidAccount340b(value)) {
        return { isValid: false, message: translator.getError("invalid_alphanumeric_20") };
      }
      return VALID;

    case "product_serialization_number":
      if (!isValidProductSerializationNumber(value)) {
        return { isValid: false, message: translator.getError("invalid_alphanumeric_20") };
      }
      return VALID;

    case "wholesaler_invoice_number":
      if (!isValidWholesalerInvoiceNumber(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_wholesaler_invoice_number"),
        };
      }
      return VALID;

    case "service_provider_id":
    case "prescriber_id":
    case "encounter_billing_service_provider_id":
    case "encounter_rendering_physician_id":
      if (!value) return VALID;
      if (!isValidIdentifier(value)) {
        return { isValid: false, message: translator.getError("invalid_identifier") };
      }
      return VALID;

    case "encounter_hcpcs_code":
      if (!isValidHCPCS(value)) {
        return { isValid: false, message: translator.getError("invalid_encounter_hcpcs_code") };
      }
      return VALID;

    case "encounter_hcpcs_code_modifier_1":
    case "encounter_hcpcs_code_modifier_2":
    case "encounter_hcpcs_code_modifier_3":
    case "encounter_hcpcs_code_modifier_4":
      if (!isValidHCPCSMod(value)) {
        return { isValid: false, message: translator.getError("invalid_alphanumeric_2") };
      }
      return VALID;

    case "encounter_diagnosis_code_primary":
    case "encounter_diagnosis_code_2":
    case "encounter_diagnosis_code_3":
    case "encounter_diagnosis_code_4":
    case "encounter_diagnosis_code_5":
    case "encounter_diagnosis_code_6":
    case "encounter_diagnosis_code_7":
    case "encounter_diagnosis_code_8":
    case "encounter_diagnosis_code_9":
    case "encounter_diagnosis_code_10":
      if (!isValidDiagnosisCode(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_diagnosis_code"),
        };
      }
      return VALID;

    case "encounter_place_of_service_code":
      if (!value) return VALID;
      if (!isValidPlaceOfService(value)) {
        return { isValid: false, message: translator.getError("invalid_place_of_service_code") };
      }
      return VALID;
  }

  return VALID;
};

const checkRequired = (column, value) => {
  if (isBlank(value) && isRequiredField(column)) {
    return { isValid: false, message: translator.getError("field_required", { column }) };
  }
  return VALID;
};

export default isValidPharmacyEncounterClaimField;
